import React, { useEffect, useRef, useState } from 'react'
import './Forms.css'

const TESTFY_API_URL =
  process.env.REACT_APP_API_URL || 'https://api.testfy.com.br'
const FORMS_ACTIVE_URL = `${TESTFY_API_URL.replace(/\/$/, '')}/public/diana/forms/active`
const FORMS_SUBMIT_URL = `${TESTFY_API_URL.replace(/\/$/, '')}/public/diana/forms/leads`

type Phase = 'loading' | 'ready' | 'submitted'

const FIELDS = [
  'name',
  'email',
  'phone',
  'specialty',
  'client',
  'consent',
] as const
type FieldId = (typeof FIELDS)[number]

const COPY: Record<FieldId, { label: string; placeholder?: string; hint?: string }> = {
  name: {
    label: 'Nome completo',
    placeholder: 'Como você se chama',
  },
  email: {
    label: 'E-mail',
    placeholder: 'voce@exemplo.com',
  },
  phone: {
    label: 'Telefone',
    placeholder: '(11) 98765-4321',
  },
  specialty: {
    label: 'Especialidade',
    placeholder: 'Ex: Nutricionista, Médica',
  },
  client: {
    label: 'Cliente Testfy',
  },
  consent: {
    label: 'Autorização de uso',
  },
}

function maskPhoneBR(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 11)
  const len = digits.length
  if (len === 0) return ''
  if (len <= 2) return `(${digits}`
  if (len <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`
  if (len <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
}

function isValidPhoneBR(value: string): boolean {
  const d = value.replace(/\D/g, '')
  return d.length === 10 || d.length === 11
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

function CheckIcon({ size = 14 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="4 12 10 18 20 6" />
    </svg>
  )
}

function FormsApp() {
  const [phase, setPhase] = useState<Phase>('loading')
  const [submitting, setSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [headline, setHeadline] = useState<string | null>(null)

  const [activeIdx, setActiveIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const activeRowRef = useRef<HTMLLIElement | null>(null)

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [specialty, setSpecialty] = useState('')
  const [isClient, setIsClient] = useState<'sim' | 'nao' | ''>('')
  const [optIn, setOptIn] = useState(false)

  // Ref que rastreia ultimo valor (evita stale closure no setTimeout do blur)
  const valuesRef = useRef({ name, email, phone, specialty, isClient, optIn })
  useEffect(() => {
    valuesRef.current = { name, email, phone, specialty, isClient, optIn }
  })

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(FORMS_ACTIVE_URL)
        if (cancelled) return
        if (res.ok) {
          const data = (await res.json()) as { description: string | null }
          if (data.description) setHeadline(data.description)
        }
        setPhase('ready')
      } catch {
        if (!cancelled) setPhase('ready')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (phase !== 'ready') return
    const t = setTimeout(() => {
      activeRowRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
      inputRef.current?.focus()
    }, 80)
    return () => clearTimeout(t)
  }, [activeIdx, phase])

  // Validacao pura: aceita o snapshot dos valores. Usado tanto no render
  // (com state direto) quanto no setTimeout do blur (com valuesRef.current).
  const checkValid = (
    f: FieldId,
    v: {
      name: string
      email: string
      phone: string
      specialty: string
      isClient: string
      optIn: boolean
    }
  ): boolean => {
    switch (f) {
      case 'name':
        return v.name.trim().length > 0
      case 'email':
        return isValidEmail(v.email)
      case 'phone':
        return isValidPhoneBR(v.phone)
      case 'specialty':
        return v.specialty.trim().length > 0
      case 'client':
        return v.isClient !== ''
      case 'consent':
        return v.optIn
    }
  }

  // Render-time: usa state direto (atualiza imediato apos setX).
  const isValid = (f: FieldId): boolean =>
    checkValid(f, { name, email, phone, specialty, isClient, optIn })

  // Async (dentro de setTimeout): usa ref pra evitar stale closure.
  const isValidLatest = (f: FieldId): boolean => checkValid(f, valuesRef.current)

  const valueOf = (f: FieldId): string => {
    switch (f) {
      case 'name':
        return name.trim()
      case 'email':
        return email.trim()
      case 'phone':
        return phone
      case 'specialty':
        return specialty.trim()
      case 'client':
        return isClient === 'sim' ? 'Sim, sou cliente Testfy' : 'Ainda não'
      case 'consent':
        return 'Autorizado'
    }
  }

  const advance = () => {
    const cur = FIELDS[activeIdx]
    // usa Latest pra cobrir o caso de chamadas async (segmented setTimeout)
    if (!isValidLatest(cur)) return
    if (activeIdx < FIELDS.length - 1) setActiveIdx(activeIdx + 1)
    else void submit()
  }

  const submit = async () => {
    if (submitting) return
    setSubmitting(true)
    setErrorMessage(null)
    try {
      const res = await fetch(FORMS_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim().toLowerCase(),
          phone: phone.trim(),
          specialty: specialty.trim(),
          is_client: isClient === 'sim',
          opt_in: optIn,
        }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.message || 'Não foi possível enviar.')
      }
      setPhase('submitted')
    } catch (err) {
      setErrorMessage((err as Error).message)
    } finally {
      setSubmitting(false)
    }
  }

  const onEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      advance()
    }
  }

  // Auto-advance: ao tirar foco do input, se valido, vai pro proximo step.
  // Cobre o "Done" do teclado mobile e tap fora do campo.
  // setTimeout >= 100ms da tempo do React commit; valuesRef garante valor fresco.
  const onBlurAdvance = () => {
    setTimeout(() => {
      if (isValidLatest(FIELDS[activeIdx])) advance()
    }, 120)
  }

  const renderInput = (f: FieldId) => {
    if (f === 'name') {
      return (
        <input
          ref={inputRef}
          type="text"
          className="tf-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={onEnter}
          onBlur={onBlurAdvance}
          autoComplete="name"
          placeholder={COPY.name.placeholder}
        />
      )
    }
    if (f === 'email') {
      return (
        <input
          ref={inputRef}
          type="email"
          className="tf-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={onEnter}
          onBlur={onBlurAdvance}
          autoComplete="email"
          placeholder={COPY.email.placeholder}
        />
      )
    }
    if (f === 'phone') {
      return (
        <input
          ref={inputRef}
          type="tel"
          className="tf-input"
          value={phone}
          onChange={(e) => setPhone(maskPhoneBR(e.target.value))}
          onKeyDown={onEnter}
          onBlur={onBlurAdvance}
          autoComplete="tel"
          inputMode="numeric"
          placeholder={COPY.phone.placeholder}
        />
      )
    }
    if (f === 'specialty') {
      return (
        <input
          ref={inputRef}
          type="text"
          className="tf-input"
          value={specialty}
          onChange={(e) => setSpecialty(e.target.value)}
          onKeyDown={onEnter}
          onBlur={onBlurAdvance}
          placeholder={COPY.specialty.placeholder}
        />
      )
    }
    if (f === 'client') {
      return (
        <div className="tf-segmented" role="radiogroup">
          <button
            type="button"
            className={`tf-seg ${isClient === 'sim' ? 'tf-seg--on' : ''}`}
            onClick={() => {
              setIsClient('sim')
              setTimeout(advance, 160)
            }}
          >
            Sim
          </button>
          <button
            type="button"
            className={`tf-seg ${isClient === 'nao' ? 'tf-seg--on' : ''}`}
            onClick={() => {
              setIsClient('nao')
              setTimeout(advance, 160)
            }}
          >
            Ainda não
          </button>
        </div>
      )
    }
    return (
      <label className="tf-consent">
        <input
          type="checkbox"
          checked={optIn}
          onChange={(e) => setOptIn(e.target.checked)}
        />
        <span>
          Autorizo a utilização da minha imagem e das minhas informações para
          os fins estabelecidos no enunciado.
        </span>
      </label>
    )
  }

  if (phase === 'loading') {
    return (
      <div className="tf-shell">
        <header className="tf-bar">
          <img
            src="/assets/images/testfy-white.svg"
            alt="Testfy"
            className="tf-bar-logo"
          />
        </header>
        <main className="tf-page">
          <div className="tf-spinner" aria-label="Carregando" />
        </main>
      </div>
    )
  }

  if (phase === 'submitted') {
    return (
      <div className="tf-shell">
        <header className="tf-bar">
          <img
            src="/assets/images/testfy-white.svg"
            alt="Testfy"
            className="tf-bar-logo"
          />
        </header>
        <main className="tf-page">
          <div className="tf-success">
            <div className="tf-success-mark" aria-hidden>
              <CheckIcon size={28} />
            </div>
            <h1 className="tf-success-title">
              Pronto, {name.split(' ')[0]}.
            </h1>
            <p className="tf-success-sub">Seu registro foi efetuado com sucesso.</p>
          </div>
        </main>
      </div>
    )
  }

  const isLast = activeIdx === FIELDS.length - 1
  const currentValid = isValid(FIELDS[activeIdx])

  return (
    <div className="tf-shell">
      <header className="tf-bar">
        <img
          src="/assets/images/testfy-white.svg"
          alt="Testfy"
          className="tf-bar-logo"
        />
      </header>

      <main className="tf-page">
        <section className="tf-intro">
          <span className="tf-eyebrow">Testfy Forms</span>
          <h1 className="tf-title">
            {headline || 'Confirme sua participação.'}
          </h1>
          <p className="tf-sub">
            {FIELDS.length} informações curtas. Levamos sua privacidade a sério —
            seus dados ficam com a Testfy.
          </p>
        </section>

        <ol className="tf-list">
          {FIELDS.map((field, idx) => {
            const done = idx < activeIdx
            const active = idx === activeIdx
            const future = idx > activeIdx
            return (
              <li
                key={field}
                ref={active ? activeRowRef : undefined}
                className={[
                  'tf-row',
                  done && 'tf-row--done',
                  active && 'tf-row--active',
                  future && 'tf-row--future',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <header className="tf-row-head">
                  <span className="tf-row-label">{COPY[field].label}</span>
                  {done && (
                    <button
                      type="button"
                      className="tf-row-edit"
                      onClick={() => setActiveIdx(idx)}
                    >
                      Editar
                    </button>
                  )}
                </header>

                {done && (
                  <div className="tf-row-value">
                    <span className="tf-row-check" aria-hidden>
                      <CheckIcon size={11} />
                    </span>
                    <span className="tf-row-value-text">{valueOf(field)}</span>
                  </div>
                )}

                {active && (
                  <div className="tf-row-input">
                    {renderInput(field)}
                    {field !== 'client' && (
                      <button
                        type="button"
                        className="tf-row-cta"
                        onClick={advance}
                        disabled={!isValid(field) || submitting}
                      >
                        {idx === FIELDS.length - 1
                          ? submitting
                            ? 'Enviando…'
                            : 'Confirmar participação'
                          : 'Continuar'}
                      </button>
                    )}
                  </div>
                )}

                {future && (
                  <div className="tf-row-skel" aria-hidden>
                    <span className="tf-row-skel-bar" />
                  </div>
                )}
              </li>
            )
          })}
        </ol>

        {errorMessage && <p className="tf-error">{errorMessage}</p>}

        <footer className="tf-foot">
          <span>by</span> <span className="tf-foot-brand">Testfy</span>
        </footer>
      </main>
    </div>
  )
}

export default FormsApp
